<template>
  <div class="facility-contract-form">
    <v-form ref="form">
      <v-alert
          border="bottom"
          colored-border
          type="warning"
          elevation="2"
          v-if="isReadonly"
      >
        <div v-if="isAdmin || couldUserEdit" >
          {{$t('book/facility:facility.dashboard.information.contracts.labels.read-only-label')}} <a target="_blank" :href="getGlobalUrl">{{$t('common:link')}}</a>.
        </div>
        <div v-else>
          {{$t('book/facility:facility.dashboard.information.contracts.labels.read-only-label-without-link')}}
        </div>
      </v-alert>
      <h3 class="font-weight-bold px-sm-5 mb-0">{{ $t( edit ? 'buttons:edit-specific' : 'buttons:add-specific', { specific: $t('common:contract')}) }}</h3>
      <v-row class="pa-sm-5" justify="space-between">
        <v-col cols="12" lg="5">
          <h4 class="primary--text font-weight-medium">{{ $t('buttons:upload-specific', { specific: $t('common:document')}) }}</h4>
          <DragAndDropFile
              ref="dragAndDrop"
              @fileChange="uploadAsUrl"
              @downloadFile="downloadFile"
              @downloadBlob="downloadBlob"
              custom-height="80vh"
              not-required
              :readOnly="isReadonly"
              :view-only="hideButton"
          />
        </v-col>

        <v-col cols="12" lg="6">
          <v-row justify="space-between">
            <v-col cols="12" md="6">
              <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
              <!-- Contract name -->
              <v-text-field
                  v-model="data.name"
                  outlined
                  clearable
                  :disabled="isReadonly"
                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.contract-name')"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
                  tabindex="62"
              />

              <!-- contract types -->
              <v-select
                  :items="contractTypes"
                  item-text="translatedName"
                  item-value="name"
                  :menu-props="{ bottom: true, offsetY: true }"
                  v-model="data.types"
                  :disabled="isReadonly"
                  outlined
                  multiple
                  chips
                  :label="$t('book/facility:facility.dashboard.information.contracts.common.contract-type')"
                  clearable
                  tabindex="63"
                  :rules="[ruleRequiredMultiple($t('utils:rules.multiselect', {name : $t('book/facility:facility.dashboard.contract.contracts.dialog-contract')}))]">
              </v-select>

              <v-checkbox
                  :label="$t('book/facility:facility.dashboard.contract.cost-pro-type')"
                  v-model="data.costProType"
                  tabindex="64"
                  :disabled="isReadonly" />

              <v-container v-if="data.costProType" :disabled="isReadonly">
                <v-row>
                  <v-radio-group
                      v-model="data.costYearly"
                      row
                      @change="calculateMonthlyYearlyPrice"
                  >
                    <v-radio
                        :label="$t('book/facility:facility.dashboard.contract.monthly')"
                        :value="false"
                    ></v-radio>
                    <v-radio
                        :label="$t('book/facility:facility.dashboard.contract.yearly')"
                        :value="true"
                    ></v-radio>
                  </v-radio-group>
                </v-row>
                <v-row v-for="type in data.types" :key="type">
                  <v-col align-self="center">{{$t('enums:contract-types.' + type)}}</v-col>
                  <v-col align-self="center">
                    <v-text-field
                        :label="$t('book/facility:facility.dashboard.information.contracts.titles.cost')"
                        append-icon="euro_symbol"
                        outlined
                        v-model="proTypeCosts[type]"
                        @input="calculateMonthlyYearlyPrice"
                        :disabled="isReadonly"
                        :rules="costRules"
                        clearable />
                  </v-col>
                </v-row>
                <v-row class="total-area">
                  <v-col>
                    {{ $t('book/facility:facility.dashboard.contract.total-monthly') }}: <span class="price">{{data.monthlyCost}}</span>
                  </v-col>
                  <v-col>
                    {{ $t('book/facility:facility.dashboard.contract.total-yearly') }}: <span class="price">{{data.yearlyCost}}</span>
                  </v-col>
                </v-row>
              </v-container>

              <div>
                <!-- Time Period Section -->
                <h4 class="primary--text font-weight-medium">{{ $t('book/facility:facility.dashboard.information.contracts.titles.date') }}</h4>
                <DateField
                    ref="start"
                    @update="(v) => updateStart(v)"
                    :isClearable="true"
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.contract-start')"
                    tabindex="65"
                    :disabled="isReadonly"
                    :key="dateStartKey"
                />

                <v-row>
                  <v-col cols="12" xl="6">
                    <v-text-field
                        v-model="data.term"
                        outlined
                        clearable
                        :label="$t('book/facility:facility.dashboard.information.contracts.labels.duration')"
                        :rules="[ruleNumberFrom(1, $t('utils:rules.number-from', {from: 1}))]"
                        type="number"
                        tabindex="66"
                        :disabled="isReadonly"
                        @input="calcEndDate"
                    />
                  </v-col>
                  <v-col cols="12" xl="6">
                    <DateField
                        ref="end"
                        @update="(v) => (data.contractEndParsed = v)"
                        :label="$t('book/facility:facility.dashboard.information.contracts.labels.contract-end')"
                        disabled
                        :key="dateEndKey"
                    />
                  </v-col>
                </v-row>

                <v-text-field
                    v-model="data.noticePeriodQuarterly"
                    outlined
                    clearable
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.notice-period')"
                    :rules="[ruleNumberFromTo(1, 4, $t('utils:rules.number-from-to', {to: '1', from: '4'}))]"
                    type="number"
                    tabindex="67"
                    :disabled="isReadonly"
                />

                <v-checkbox
                    v-model="data.automaticRenewal"
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.renewal')"
                    color="secondary"
                    :disabled="isRenewalDisabled"
                    tabindex="68"
                />

                <v-text-field v-if="data.automaticRenewal"
                    v-model="data.renewalYears"
                    outlined
                    clearable
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.renewal-years')"
                    :rules="[ruleNumberFromTo(1, 99, $t('utils:rules.number-from-to', {to: '1', from: '99'}))]"
                    type="number"
                    tabindex="69"
                    :disabled="isRenewalDisabled"
                />
              </div>
            </v-col>
            <v-col cols="12" md="5">
              <!-- Cost Section -->
              <h4 class="primary--text font-weight-medium">{{ $t('book/facility:facility.dashboard.information.contracts.titles.cost') }}</h4>

              <v-radio-group
                  v-model="data.costsInNet"
                  row
                  :disabled="isReadonly"
              >
                <v-radio
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.gross')"
                    :value="false"
                    tabindex="72"
                ></v-radio>
                <v-radio
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.net')"
                    :value="true"
                    tabindex="73"
                ></v-radio>
              </v-radio-group>

              <v-text-field
                  v-if="!data.costsInNet"
                  v-model="data.vatRate"
                  :disabled="isReadonly"
                  outlined
                  clearable
                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.vat-rate')"
                  :rules="[
                    ruleNotNegative($t('utils:rules.negative-value')),
                    ruleIsInteger($t('utils:rules.onlynumerictext'))
                  ]"
              />

              <!-- Cost Section -->
              <div v-if="!data.costProType">
                <v-radio-group
                    v-model="data.costYearly"
                    row
                    :disabled="isReadonly"
                >
                  <v-radio
                      :label="$t('book/facility:facility.dashboard.contract.monthly')"
                      :value="false"
                      tabindex="74"
                  ></v-radio>
                  <v-radio
                      :label="$t('book/facility:facility.dashboard.contract.yearly')"
                      :value="true"
                      tabindex="75"
                  ></v-radio>
                </v-radio-group>

                <v-text-field
                    v-model="data.monthlyCost"
                    outlined
                    clearable
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.monthly-cost')"
                    :rules="costRules"
                    type="number"
                    append-icon="euro_symbol"
                    tabindex="76"
                    @input="calcYearlyCosts"
                    :disabled="isReadonly || data.costYearly"
                />

                <v-text-field
                    v-model="data.yearlyCost"
                    outlined
                    clearable
                    :label="$t('book/facility:facility.dashboard.information.contracts.labels.yearly-cost')"
                    :rules="costRules"
                    type="number"
                    @input="calcMonthlyCost"
                    append-icon="euro_symbol"
                    tabindex="77"
                    :disabled="isReadonly || !data.costYearly"
                />
              </div>

              <!-- Contract Section -->
              <h4 class="primary--text font-weight-medium">{{ $t('book/facility:facility.dashboard.information.contracts.titles.contract') }}</h4>
              <CustomerTypeAhead
                  ref="manufacturerTypeahead"
                  :value="data.company"
                  @input="data.company = $event"
                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.company')"
                  tabindex="70"
                  :disabled="isReadonly"
              />

              <v-text-field
                  v-model="data.unknownCompany"
                  outlined
                  clearable
                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.unknown-company')"
                  tabindex="71"
                  :disabled="isReadonly"
              />
            </v-col>
          </v-row>
          <v-row>
            <FacilityList v-if="isGlobal"
                ref="facilityList"
                @facilitiesChange="v => facilityIds = v"
                @companyChange="v => data.globalCompany = v"
            />
          </v-row>
          <v-row :disabled="isReadonly">
            <v-col>
              <h4 class="primary--text font-weight-medium">{{ $t('book/facility:facility.dashboard.information.contracts.labels.note') }}</h4>
              <v-textarea
                  outlined
                  clearable
                  v-model="data.note"
                  tabindex="78"
                  :disabled="isReadonly"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-expansion-panels accordion tile multiple :disabled="isReadonly">
                <v-expansion-panel class="mb-5">
                  <v-expansion-panel-header>
                    <h5 class="primary--text font-weight-medium mb-0">{{ $t('book/facility:facility.dashboard.information.contracts.common.parameter') }}</h5>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content eager class="px-3">
                    <!-- Parameter-Table -->
                    <v-col cols="12">
                      <DataTable
                          :headers="parameterHeader"
                          :data="selectedParameter"
                          icon="create"
                          @clickedButton1="editParameter"
                          icon2="delete"
                          iconColor2="error"
                          @clickedButton2="removeParameter"
                          sortBy="name"
                      />
                    </v-col>

                    <!-- Parameter-Input-Fields -->
                    <v-col cols="12" sm="11">
                      <h6 class="greyMedium--text font-weight-bold">{{ $t('buttons:edit-specific', { specific: $t('book/facility:facility.dashboard.information.contracts.common.parameter')}) }}</h6>
                      <v-row>
                        <v-col v-if="singleParameter.useValue" cols="9" md="5">
                          <v-select
                              v-model="singleParameter.parameter"
                              outlined
                              item-text="name"
                              item-value="id"
                              :label="$t('book/facility:facility.dashboard.information.contracts.common.parameter')"
                              :menu-props="{ bottom: true, offsetY: true }"
                              :items="parameterForSelection"
                              @change="checkUseValue(singleParameter.parameter)"
                          />
                        </v-col>
                        <v-col v-else cols="10" md="5">
                          <v-select
                              v-model="singleParameter.parameter"
                              outlined
                              item-text="name"
                              item-value="id"
                              :label="$t('book/facility:facility.dashboard.information.contracts.common.parameter')"
                              :menu-props="{ bottom: true, offsetY: true }"
                              :items="parameterForSelection"
                              @change="checkUseValue(singleParameter.parameter)"
                          />
                        </v-col>
                        <v-col v-if="singleParameter.useValue" cols="9" md="5">
                          <v-radio-group v-show="singleParameter.valueType === 'BOOLEAN'" v-model="singleParameter.value" row class="mt-0 mt-sm-4">
                            <v-radio
                                key="1"
                                :label="$t('book/facility:facility.dashboard.information.contracts.labels.radio-1')"
                                value="true"
                            />

                            <v-radio
                                key="0"
                                :label="$t('book/facility:facility.dashboard.information.contracts.labels.radio-2')"
                                value="false"
                                color="error"
                            />
                          </v-radio-group>

                          <v-text-field
                              v-if="singleParameter.valueType === 'TEXT'"
                              v-model="singleParameter.value"
                              :label="$t('book/facility:common.text')"
                              outlined
                              type="text"
                          />

                          <v-text-field
                              v-if="singleParameter.valueType === 'INTEGER'"
                              v-model="singleParameter.value"
                              :label="$t('book/facility:facility.dashboard.information.contracts.labels.integer')"
                              outlined
                              type="number"
                              :rules="[ruleIsInteger($t('utils:rules.onlynumerictext'))]"
                          />

                          <v-text-field
                              v-else-if="singleParameter.valueType === 'DECIMAL'"
                              v-model="singleParameter.value"
                              :label="$t('book/facility:facility.dashboard.information.contracts.labels.decimal')"
                              outlined
                              type="number"
                          />

                          <v-row v-else-if="singleParameter.valueType === 'COUNTER'">
                            <v-col cols="6" class="py-0">
                              <v-text-field
                                  v-model="singleParameter.counterVal1"
                                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.counter-1')"
                                  type="number"
                                  outlined
                              />
                            </v-col>
                            <v-col cols="6" class="py-0">
                              <v-text-field
                                  v-model="singleParameter.counterVal2"
                                  :label="$t('book/facility:facility.dashboard.information.contracts.labels.counter-2')"
                                  type="number"
                                  outlined
                              />
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col v-if="singleParameter.useValue" cols="3" sm="1">
                          <v-btn
                              v-show="singleParameter.parameter && (singleParameter.value || !singleParameter.useValue || (singleParameter.counterVal1 && singleParameter.counterVal1))"
                              class="mt-4"
                              icon
                              color="success"
                              @click="addParameter()"
                          >
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col v-else cols="2">
                          <v-btn
                              v-show="singleParameter.parameter && (singleParameter.value || !singleParameter.useValue || (singleParameter.counterVal1 && singleParameter.counterVal1))"
                              class="mt-4"
                              icon
                              color="success"
                              @click="addParameter()"
                          >
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel class="mb-5">
                  <v-expansion-panel-header>
                    <h5 class="primary--text font-weight-medium mb-0">{{ $t('book/facility:facility.dashboard.information.contracts.titles.hourly-rates') }}</h5>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="px-3">
                    <DataTable
                        :headers="hourlyRateHeader"
                        :data="selectedRates"
                        icon="create"
                        @clickedButton1="editRate"
                        icon2="delete"
                        iconColor2="error"
                        @clickedButton2="removeRate"
                        sortBy="translatedName"
                    />

                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select
                            v-model="singleRate.type"
                            outlined
                            item-text="translatedName"
                            item-value="name"
                            :label="$t('book/facility:facility.dashboard.information.contracts.common.type')"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :items="hourlyRatesForSelection"
                        />
                      </v-col>
                      <v-col cols="12" sm="5">
                        <v-text-field
                            v-model="singleRate.rate"
                            outlined
                            :label="$t('book/facility:facility.dashboard.information.contracts.common.rate')"
                            type="number"
                            append-icon="euro_symbol"
                        />
                      </v-col>
                      <v-col cols="2" sm="1">
                        <v-btn
                            class="mt-3"
                            icon
                            color="success"
                            @click="addRate"
                        >
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
          <v-row v-show="!hideButton">
            <BaseButton
                color="primary"
                :btnText="edit ? $t('buttons:save-changes')
                    : $t('buttons:add-specific', { specific: $t('common:contract') })"
                @clickedButton="isGlobal ? updateForm() : uploadDocumentation()"
                :disabled="isReadonly"
            />

            <DirtyConfirmButtonDialog
                ref="dirtyDialog"
                :btn-text="$t('buttons:cancel')"
                color="grey"
                :dialog-title="$t('dialog:dirty-save-btn-title')"
                :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                :button1-text="$t('dialog:dirty-save-btn-1-text')"
                :button2-text="$t('dialog:dirty-save-btn-2-text')"
                @clickedButton="cancelDialog"
                :dataToWatch="data"
                hide-overlay
            />
          </v-row>
        </v-col>
        <v-col md="5"></v-col>
      </v-row>
    </v-form>
  </div>
</template>
<script>
import DataTable from '@/modules/core/components/DataTable/DataTable.vue';
import FacilityList from '@/modules/book/views/global-documentation/contract/form/FacilityList.vue';
import CustomerTypeAhead from '@/modules/core/components/CustomerTypeAhead.vue';
import DirtyConfirmButtonDialog from '@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog.vue';
import DateField from '@/modules/core/components/DateField.vue';
import BaseButton from '@/modules/core/components/Base/BaseButton.vue';
import DragAndDropFile from '@/modules/core/components/DragAndDropFile.vue';
import ContractInfo from '@/modules/book/data/contract-info';
import {
  ruleIsInteger,
  ruleNotNegative,
  ruleNumberFrom,
  ruleNumberFromTo,
  ruleRequired, ruleRequiredMultiple
} from '@/modules/core/services/rules';
import DocumentationService from '@/modules/core/services/documentation-service';
import FacilityDocumentation from '@/modules/book/data/facility-documentation';
import Utils from '@/modules/core/services/utils';
import ParameterValue from '@/modules/book/data/parameter-value';
import ContractInfoService from '@/modules/book/services/contract-info-service';
import GlobalDocumentationService from '@/modules/book/services/global-documentation-service';
import DataUtils from '@/modules/core/services/data-utils';
import ParameterService from '@/modules/book/services/parameter-service';
import Parameter from '@/modules/book/data/parameter';
import InfoService from '@/modules/core/services/info-service';
import {DocumentType} from '@/modules/book/data/document-type';
import DateUtils from '@/modules/core/services/date-utils';
import CustomerService from '@/modules/core/services/customer-service';
import Customer from '@/modules/core/data/customer';

export default {
  name: 'FacilityContractForm',
  components: {
    DragAndDropFile,
    DateField,
    CustomerTypeAhead,
    DataTable,
    BaseButton,
    DirtyConfirmButtonDialog,
    FacilityList
  },
  data() {
    return {
      data: new ContractInfo(),

      restCustomer: [],
      restParameter: [],
      parameterForSelection: [],
      selectedParameter: [],
      singleParameter: {
        id: null,
        onwer: null,
        name: null,
        parameter: null,
        useValue: null,
        valueType: null,
        subType: null,
        typeName: null,
        value: null,
        counterVal1: null,
        counterVal2: null
      },
      restRates: [],
      hourlyRatesForSelection: [],
      selectedRates: [],
      singleRate: {
        type: null,
        translatedName: null,
        rate: null
      },
      deleteContractId: null,
      edit: false,
      tableOptions: [],
      tableSearch: [],
      contractTypes:  [],
      parsedType: null,

      dateEndKey: 0,
      dateStartKey: 1,
      searchTimer: null,
      fileToSave: null,
      facilityIds: [],
      facilityId: null,
      // change note kopp: use this.contractStartParsed instead of this.data.contractStartParsed to be reactive
      // contractStartParsed needs to be reactive, because it is used in widgets for automatic renewal
      // :disabled="isRenewalDisabled"
      contractStartParsed: null,
      proTypeCosts: []
    }
  },
  props: {
    isGlobal: Boolean,
    hideButton: Boolean
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },

    isRenewalDisabled() {
      return !this.contractStartParsed || !this.data.term || this.isReadonly;
    },

    parameterHeader() {
      return [
        {text: this.$t('book/facility:facility.dashboard.information.contracts.common.parameter'), value: 'name'},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.datatable.parameter-meta'), value: 'typeName'},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.datatable.parameter-sub-type'), value: 'subType'},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.datatable.parameter-value'), value: 'value'},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.datatable.parameter-type'), value: 'valueType'},
        {text: "", value: 'controls', sortable: false},
      ]
    },

    hourlyRateHeader() {
      return [
        {text: this.$t('book/facility:facility.dashboard.information.contracts.common.type'), value: 'translatedName'},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.common.rate'), value: 'rate'},
        {text: '', value: 'controls'}
      ]
    },
    isAdmin() {
      return this.$store.getters["auth/isAdmin"];
    },
    isReadonly() {
      return this.data && typeof this.data.parentContract != 'undefined' && this.data.parentContract !== null;
    },
    couldUserEdit() {
      return this.data
          && this.data.globalCompany
          && (this.$store.state.auth.profile.customer === this.data.globalCompany);
    },
    getGlobalUrl() {
      let url = this.$router.resolve({
        name: "ContractList"
      });
      return url.href + '?contractId=' + this.data.parentContract;
    },
    costRules() {
      return [this.ruleNotNegative(this.$t('utils:rules.negative-value')), this.ruleRequired(this.$t('utils:rules.required'))];
    }
  },
  methods: {
    ruleNotNegative,
    ruleRequired,
    ruleIsInteger,
    ruleNumberFromTo,
    ruleNumberFrom,
    ruleRequiredMultiple,

    updateStart(newDate) {
      let oldDate = this.contractStartParsed;
      this.contractStartParsed = newDate;
      if (oldDate !== newDate) {
        this.calcEndDate();
      }
    },
    downloadFile(document) {
      DocumentationService.downloadFile(document.id,
          document.fileName,
          document.fileType,
          this.data.facilityId,
          document.documentType)
    },
    downloadBlob(document) {
      DocumentationService.downloadBlob(document.fileName, document.src);
    },
    uploadDocumentation() {
      if (!this.isFormValid()) return;

      if (!this.fileToSave) {
       this.updateForm();
       return;
      }

      let docu = new FacilityDocumentation();
      docu.name = this.fileToSave.name;
      docu.start = "";
      docu.end = "";
      docu.type = "CONTRACT"

      let facilityId = this.facilityId ? this.facilityId : this.$route.params.id;
      DocumentationService.postFacilityDocumentationToAPIWithProgress(
          facilityId,
          this.fileToSave,
          docu,
          (event) => {
            this.$refs.dragAndDrop.progress = Math.round((100 * event.loaded) / event.total);
          },
          (res) => {
            this.data.documentation = res.data.id;
            this.updateForm();
            Utils.showSuccessSnackbar(this.$t('snackbar:upload-success', {name: this.$t('common:documentation')}))
          },
          (error) => {
            this.$logger.error("Could not upload facility documentation for contract because ", error);
            this.$refs.dragAndDrop.isUploading = false;
            Utils.showFailureSnackbar(this.$t('snackbar:upload-failure', {name: this.$t('common:documentation')}))
          }
      );

    },
    updateForm() {
      if (!this.isFormValid()) return;

      let toPush = [];

      if (this.data.costProType) {
        this.data.proTypeCosts = this.convertTypeCostForSave(this.proTypeCosts);
      }

      for(var i = 0; i < this.selectedParameter.length; i++) {
        var pVal = new ParameterValue();

        pVal.id = this.selectedParameter[i].id;
        pVal.owner = this.selectedParameter[i].owner;
        pVal.parameter = this.selectedParameter[i].parameter;
        pVal.value = this.selectedParameter[i].value;

        toPush.push(pVal);
      }

      let hourlyRates = [];
      for (let i = 0; i < this.selectedRates.length; i++) {
        let rate = {
          type: this.selectedRates[i].type,
          rate: this.selectedRates[i].rate,
          contract: this.selectedRates[i].contract
        }

        hourlyRates.push(rate);
      }

      this.data.parameters = toPush;
      this.data.hourlyRates = hourlyRates;
      this.data.contractStart = this.contractStartParsed;
      this.data.contractEnd = this.data.contractEndParsed;

      if (this.isGlobal) {
        this.saveGlobalContract();
        return;
      }

      let facilityId = this.facilityId ? this.facilityId : this.$route.params.id;
      ContractInfoService.putToAPI(facilityId, this.data,
          () => {
            this.$emit('dialogClosed');
            this.$emit('updateContractData', this.tableOptions, this.tableSearch)

            if(this.edit) {
              Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:contract')}))
            } else {
              Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:contract')}))
            }
          },
          () => {
            this.$emit('dialogClosed');
            if(this.edit) {
              Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:contract')}))
            } else {
              Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:contract')}))
            }
          })

      this.cleanupDialogForm();
    },

    saveGlobalContract() {
      let item = this.data;
      GlobalDocumentationService.createOrSaveGlobalContract(item, this.facilityIds, this.fileToSave,
          () => {
            if (item.id) {
              Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:contract')}))
            } else {
              Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:contract')}))
            }
            this.$emit('dialogClosed');
          },
          () => {
            if(this.edit) {
              Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:contract')}))
            } else {
              Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:contract')}))
            }
            this.$emit('dialogClosed');
          }
      );

      this.cleanupDialogForm();
    },

    isFormValid() {
      // because `||` is a short-circuits operator, when some condition return true the rests operators don't execute,
      // but we need to check at the same time all conditions to highlight not filled out fields.
      if (this.isGlobal) {
        this.$refs.facilityList.isMandatoryFilledOut();
      }
      this.$refs.dragAndDrop.isMandatoryFilledOut();
      if (!this.$refs.form.validate()
          || this.isGlobal && !this.$refs.facilityList.isMandatoryFilledOut()
          || !this.$refs.dragAndDrop.isMandatoryFilledOut()
      ) {
        document.getElementsByClassName('v-dialog--active')[0].scrollTop = 0;
        return false;
      }

      return true;
    },
    // clean up method for dialog form
    // NOTE this.$refs.form.reset() not working because dataTable is then messed up
    cleanupDialogForm() {
      this.data = new ContractInfo();
      this.singleParameter = {

        id: null,
        onwer: null,
        name: null,
        parameter: null,
        useValue: null,
        valueType: null,
        subType: null,
        typeName: null,
        value: null,
        counterVal1: null,
        counterVal2: null
      };
      this.selectedParameter = [];
      this.parameterForSelection = [...this.restParameter];

      this.singleRate = {
        type: null,
        translatedName: null,
        rate: null
      }
      this.selectedRates = [];
      this.hourlyRatesForSelection = [...this.restRates];

      this.dateStartKey++;
      this.dateEndKey--;
      this.$refs.start.resetData();
      this.$refs.end.resetData();

      this.parsedType = null;

      this.$refs.dragAndDrop.setPreviewSrc(null);
      this.$refs.dragAndDrop.file = null;
      this.$refs.form.resetValidation();
      this.facilityIds = [];
      this.fileToSave = null;
      this.proTypeCosts = [];

      if (this.isGlobal) {
        this.$refs.facilityList.clear();
      }
    },

    openNewContractDialog(options, search) {
      this.tableOptions = options;
      this.tableSearch = search;
      this.data = new ContractInfo();
      this.$refs.dirtyDialog.startWatch();
      this.edit = false;
    },

    cancelDialog() {
      this.$emit('cancelDialog');
      this.cleanupDialogForm();
    },

    editGlobalContract(facilityBuildList, item, option, search) {
      this.editContract(item, option, search);
      this.$refs.facilityList.setSelectedItems(facilityBuildList, item);
    },

    // fill up form for this contractInfo item
    editContract(item, options, search) {
      this.tableOptions = options;
      this.tableSearch = search;

      this.data = DataUtils.parseDataToSingleObject(item, ContractInfo);
      this.$refs.dirtyDialog.startWatch();

      // if monthly costs is filled out but not yearly costs
      // calculate the yearly costs
      // nextTick is needed, so the form is dirty and the user have to save
      // the calculated yearly costs
      if (this.data.monthlyCost && !this.data.yearlyCost) {
        this.$nextTick(() => {
          this.data.yearlyCost = (this.data.monthlyCost * 12).toFixed(2);
        });
      }

      this.data.yearlyCost = this.data.yearlyCost
          ? parseFloat(this.data.yearlyCost).toFixed(2)
          : this.data.yearlyCost;

      this.data.monthlyCost = this.data.monthlyCost
          ? parseFloat(this.data.monthlyCost).toFixed(2)
          : this.data.monthlyCost;

      //format dates
      if (this.data.contractStart) {
        this.contractStartParsed = this.$refs.start.parseDTO(this.data.contractStart);
      }

      if (this.data.contractEnd) {
        this.data.contractEndParsed = this.$refs.end.parseDTO(this.data.contractEnd);
      }

      this.$refs.manufacturerTypeahead.initialise(this.data.company);
      this.proTypeCosts = this.convertTypeCostForDisplay(this.data.proTypeCosts);

      this.selectedParameter = []
      ParameterService.readAllFromAPI(
          (resP) => {
            let data = DataUtils.parseDataToObjectArray(resP.data, Parameter)
            this.restParameter = data;
            this.parameterForSelection = [...data];

            if(this.data.parameters) {
              for(var i = 0; i < this.restParameter.length; i++) {
                for(var j = 0; j < this.data.parameters.length; j++) {
                  if(this.restParameter[i].id === this.data.parameters[j].parameter) {
                    this.selectedParameter.push({
                      id: this.data.parameters[j].id,
                      owner: this.data.parameters[j].owner,
                      name: this.restParameter[i].name,
                      parameter: this.data.parameters[j].parameter,
                      useValue: this.restParameter[i].useValue,
                      valueType: this.restParameter[i].valueType,
                      subType: this.restParameter[i].subType,
                      typeName: this.restParameter[i].typeName,
                      value: this.data.parameters[j].value,
                      counterVal1: (this.restParameter[i].valueType === 'COUNTER') ? this.data.parameters[j].value.split(';')[0] : null,
                      counterVal2: (this.restParameter[i].valueType === 'COUNTER') ? this.data.parameters[j].value.split(';')[1] : null
                    })

                    this.removeParameterFromSelection(this.restParameter[i].id);
                  }
                }
              }
            }
          },
          (error) => {
            this.$logger.error("Error at ParameterService.readAllFromAPI" + error)
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/facility:facility.dashboard.information.contracts.snackbar.contract-data')}))
          }
      )

      this.selectedRates = []
      InfoService.readAndTranslateEnum(
          'contract/hourlyType',
          'enums:contract-hourly-rate-types.',
          (res) => {
            this.restRates = Utils.sort(res, false, 'translatedName');
            this.hourlyRatesForSelection = [...this.restRates];

            if (this.data.hourlyRates) {
              for (let i = 0; i < this.restRates.length; i++) {
                for (let j = 0; j < this.data.hourlyRates.length; j++) {
                  if (this.restRates[i].name === this.data.hourlyRates[j].type) {
                    this.selectedRates.push({
                      type: this.data.hourlyRates[j].type,
                      translatedName: this.restRates[i].translatedName,
                      rate: this.data.hourlyRates[j].rate,
                      contract: this.data.hourlyRates[j].contract
                    })

                    this.removeRateFromSelection(this.restRates[i].name);
                  }
                }
              }
            }
          }
      )

      if (this.data.documentation) {
        DocumentationService.readInfoFromApi(this.data.documentation,
            (res) => {
              let docuToParse = res.data;
              DocumentationService.getDocByTypeAndId(
                  res.data.id,
                  res.data.fileType,
                  res.data.global ? DocumentType.GLOBAL : res.data.documentType,
                  res.data.global ? res.data.customer : this.data.facility,
                  (fileUrl) => {
                    docuToParse.src = fileUrl;
                    this.$refs.dragAndDrop.setPreviewSrc(docuToParse);
                  }
              )
            }
        )
      }

      this.edit = true;
    },

    // when parameter is selected, set according name, useValue and valueType
    // for further inputs
    checkUseValue(selection) {
      for(var i = 0; i < this.restParameter.length; i++) {
        if(this.restParameter[i].id === selection) {
          this.singleParameter.name = this.restParameter[i].name;
          this.singleParameter.useValue = this.restParameter[i].useValue;
          this.singleParameter.valueType = this.restParameter[i].valueType;
          this.singleParameter.typeName = this.restParameter[i].typeName;
          this.singleParameter.subType = this.restParameter[i].subType;
        }
      }
    },

    // add parameter with opt. values to parameter dataTable
    // and remove this parameter from the selection
    addParameter() {
      if(this.singleParameter.counterVal1 && this.singleParameter.counterVal2) {
        this.singleParameter.value = this.singleParameter.counterVal1 + ";" + this.singleParameter.counterVal2;
      }
      this.selectedParameter.push(this.singleParameter)
      this.removeParameterFromSelection(this.singleParameter.parameter);
      this.singleParameter = {};
    },

    // removes parameter from v-select
    // so user cant select same parameters
    removeParameterFromSelection(id) {
      for(var i = 0; i < this.parameterForSelection.length; i++) {
        if(this.parameterForSelection[i].id === id) {
          this.parameterForSelection.splice(i, 1);
        }
      }
    },

    // fills up input form for this parameter
    // and removes it from table for easier handling
    editParameter(item) {
      if(item.valueType === 'COUNTER') {
        item.counterVal1 = item.value.split(';')[0];
        item.counterVal2 = item.value.split(';')[1];
      }
      Object.assign(this.singleParameter, item)
      this.removeParameter(item)
    },

    // removes parameter from table and adds it to v-select
    // so user can select it again
    removeParameter(item) {
      for(var i = 0; i < this.restParameter.length; i++) {
        if(this.restParameter[i].id === item.parameter) {
          this.parameterForSelection.push(this.restParameter[i]);
        }
      }

      for(var j = 0; j < this.selectedParameter.length; j++) {
        if(this.selectedParameter[j].parameter === item.parameter) {
          this.selectedParameter.splice(j, 1);
        }
      }
    },

    // add rate with value to datatable
    // and removes this parameter from the availabel hourly rates
    addRate() {
      for (let i = 0; i < this.restRates.length; i++) {
        if (this.restRates[i].name === this.singleRate.type) {
          this.singleRate.translatedName = this.restRates[i].translatedName;
        }
      }
      this.selectedRates.push(this.singleRate);
      this.removeRateFromSelection(this.singleRate.type);
      this.singleRate = {};
    },

    // removes rate form v-select
    // so user can't select it again
    removeRateFromSelection(name) {
      for (let i = 0; i < this.hourlyRatesForSelection.length; i++) {
        if (this.hourlyRatesForSelection[i].name === name) {
          this.hourlyRatesForSelection.splice(i, 1);
        }
      }
    },

    editRate(item) {
      Object.assign(this.singleRate, item)
      this.removeRate(item);
    },

    removeRate(item) {
      for (let i = 0; i < this.restRates.length; i++) {
        if (this.restRates[i].name === item.type) {
          this.hourlyRatesForSelection.push(this.restRates[i]);
        }
      }

      for (let i = 0; i < this.selectedRates.length; i++) {
        if (this.selectedRates[i].type === item.type) {
          this.selectedRates.splice(i, 1);
        }
      }
    },

    calcEndDate() {
      if (this.data.term && this.contractStartParsed) {
        const termAsNumber = Number(this.data.term);
        if (termAsNumber && termAsNumber > 0) {
          this.$refs.end.isLoading = true;

          // INFO we had to use a timout here, because otherwhise the endDate could not be calculated right
          // Example: When the term '12' has been entered and the startDate is e.g. '12.12.2022'
          // the calculated endDate was '12.12.2023'
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => {
            // use try-catch to capture incomplete contractStartParsed
            try {
              this.$refs.end.parseDTO(DateUtils.addYearsToDate(this.contractStartParsed, this.data.term));
              this.$refs.end.isLoading = false;
            } catch (e) {
              this.dateEndKey--;
              this.$refs.end.resetData();
              this.$refs.end.isLoading = false;
            }
          });
        }
      } else {
        this.dateEndKey--;
        this.$refs.end.resetData();
      }
    },

    calcYearlyCosts() {
      if (this.data.monthlyCost) {
        this.data.yearlyCost = this.data.monthlyCost * 12;
      }
    },
    calcMonthlyCost() {
      if (this.data.yearlyCost && this.data.yearlyCost > 0) {
        this.data.monthlyCost = (parseFloat(this.data.yearlyCost) / 12).toFixed(2);
      }
    },
    loadEnums() {
      CustomerService.readAllFromAPI(
          (resC) => {
            this.restCustomer = DataUtils.parseDataToObjectArray(resC.data, Customer);
          },
          (error) => {
            this.$logger.error("Error at CustomerService.readAllFromAPI" + error)
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/facility:facility.dashboard.information.contracts.snackbar.contract-data')}));
          }
      )

      ParameterService.readAllFromAPI(
          (resP) => {
            let data = DataUtils.parseDataToObjectArray(resP.data, Parameter)
            this.restParameter = data;
            this.parameterForSelection = [...data];
          },
          (error) => {
            this.$logger.error("Error at ParameterService.readAllFromAPI" + error)
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/facility:facility.dashboard.information.contracts.snackbar.contract-data')}))
          }
      )

      InfoService.readAndTranslateEnum(
          'contract/type',
          "enums:contract-types.",
          (res) => {
            this.contractTypes = Utils.sort(res, false, 'translatedName');
          }
      )

      InfoService.readAndTranslateEnum(
          'contract/hourlyType',
          'enums:contract-hourly-rate-types.',
          (res) => {
            this.restRates = Utils.sort(res, false, 'translatedName');
            this.hourlyRatesForSelection = [...this.restRates];
          }
      )

      if(!this.edit) {
        this.data = new ContractInfo();
        this.$refs.dirtyDialog.startWatch();

        if (this.parsedType) {
          this.data.types.push(this.parsedType);
        }
      }
    },
    uploadAsUrl(file) {
      if (!file) {
        return;
      }

      this.fileToSave = file;
      this.$refs.dragAndDrop.uploadAsUrl(file);
    },
    setFacilityId(facilityId) {
      this.facilityId = facilityId;
    },
    calculateMonthlyYearlyPrice() {
      let totalPrice = 0;

      for (const [type] of Object.entries(this.proTypeCosts)) {
        totalPrice += parseFloat(this.proTypeCosts[type]);
      }

      this.data.yearlyCost = (this.data.costYearly ? totalPrice : totalPrice * 12).toFixed(2);
      this.data.monthlyCost = (this.data.costYearly ? totalPrice / 12 : totalPrice).toFixed(2);
    },
    convertTypeCostForSave(costProTypeList) {
      if (!costProTypeList) return;

      let costProTypeListSave = [];
      for (const [type] of Object.entries(costProTypeList)) {
        costProTypeListSave.push({cost: costProTypeList[type], contractType: type});
      }

      return costProTypeListSave;
    },
    convertTypeCostForDisplay(costProTypeList) {
      let costToDisplay = [];
      if (!costProTypeList || costProTypeList.length == 0) return costToDisplay;

      for(let i = 0; i < costProTypeList.length; i++) {
        let item = costProTypeList[i];
        costToDisplay[item.contractType] = item.cost;
      }

      return costToDisplay;

    },
    setParsedType(parsedType) {
      this.parsedType = parsedType;
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    }
  }
}
</script>

<style lang="scss">
  .facility-contract-form {
    .total-area {
      font-size: 16px;
      .price {
        color: var(--v-primary-base);
      }
    }
  }
</style>